import { FaTimes, FaPause, FaPlay, FaEdit, FaMinus } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { UploadProgressProps } from './types';
import styles from './UploadProgress.module.css';

/**
 * アップロード進捗を表示するフローティングコンポーネント
 */
const UploadProgress = ({
    id,
    fileName,
    progress,
    status,
    error,
    videoId,
    onClose,
    onDismiss,
    onPause,
    onResume,
    onCancel
}: UploadProgressProps) => {
    // 進捗率を計算（0-100%）
    const progressPercentage = Math.min(100, Math.max(0, progress));

    // ファイル名が長い場合は省略
    const displayFileName = fileName.length > 25
        ? `${fileName.substring(0, 25)}...`
        : fileName;

    // ステータスに応じたテキスト
    const statusTextMap: Record<string, string> = {
        pending: '待機中',
        completed: '完了',
        error: 'エラー',
        cancelled: 'キャンセル済み',
        paused: '一時停止中'
    };
    const statusText = statusTextMap[status] ?? `${progressPercentage.toFixed(0)}%`;

    const isOpen = true; // このコンポーネントがレンダリングされている時点でOpenとする
    const isCompleted = status === 'completed';
    const sessionFallbackId = !id.startsWith('upload-')
        ? (id.startsWith('session_') ? id : `session_${id}`)
        : undefined;
    const editTargetId = isCompleted ? (videoId || sessionFallbackId) : undefined;
    const showEditLink = isCompleted && !!editTargetId;
    const showEditPending = isCompleted && !editTargetId;
    const canDismiss = typeof onDismiss === 'function';

    return (
        <div
            className={`${styles.uploadProgress} ${isOpen ? styles.uploadProgress__open : ''}`}
            data-testid={`upload-progress-${id}`}
        >
            <div className={styles.uploadProgress__content}>
                <div className={styles.uploadProgress__header}>
                    <div className={styles.uploadProgress__fileName} title={fileName}>
                        {displayFileName}
                    </div>

                    <div className={styles.uploadProgress__controls}>
                        {/* 一時停止/再開ボタン（アップロード中のみ） */}
                        {status === 'uploading' && (
                            <button
                                className={styles['uploadProgress__controlBtn']}
                                onClick={onPause}
                                title="一時停止"
                            >
                                <FaPause size={10} />
                            </button>
                        )}
                        {status === 'paused' && (
                            <button
                                className={styles['uploadProgress__controlBtn']}
                                onClick={onResume}
                                title="再開"
                            >
                                <FaPlay size={10} />
                            </button>
                        )}

                        {/* キャンセルボタン（完了・エラー・キャンセル済み以外） */}
                        {['uploading', 'paused', 'pending'].includes(status) && (
                            <button
                                className={styles['uploadProgress__controlBtn']}
                                onClick={onCancel}
                                title="キャンセル"
                            >
                                <MdCancel size={12} />
                            </button>
                        )}

                        {/* 編集ボタン（完了時） */}
                        {showEditLink && (
                            <Link
                                to={`/videos/${editTargetId}/edit`}
                                className={styles['uploadProgress__controlBtn']}
                                title="編集"
                            >
                                <FaEdit size={12} />
                            </Link>
                        )}
                        {showEditPending && (
                            <button
                                type="button"
                                className={styles['uploadProgress__controlBtn']}
                                disabled
                                aria-label="編集準備中"
                                title="編集準備中"
                            >
                                <FaEdit size={12} />
                            </button>
                        )}

                        {canDismiss && (
                            <button
                                className={styles['uploadProgress__controlBtn']}
                                onClick={onDismiss}
                                aria-label="閉じる"
                                title="閉じる"
                            >
                                <FaTimes size={12} />
                            </button>
                        )}

                        {/* 最小化ボタン */}
                        <button
                            className={styles['uploadProgress-closeBtn']}
                            onClick={onClose}
                            aria-label="最小化"
                            title="最小化"
                        >
                            <FaMinus />
                        </button>
                    </div>
                </div>

                <div className={styles.uploadProgress__progressContainer}>
                    <div className={styles.uploadProgress__progressBar}>
                        <div
                            className={styles.uploadProgress__progressFill}
                            data-status={status}
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>
                    <div className={styles.uploadProgress__progressInfo}>
                        <span className={styles.uploadProgress__percentage} data-status={status}>
                            {statusText}
                        </span>
                        {showEditLink ? (
                            <Link
                                to={`/videos/${editTargetId}/edit`}
                                className={styles.uploadProgress__editLink}
                                title="編集画面へ"
                            >
                                編集
                            </Link>
                        ) : showEditPending ? (
                            <span
                                className={styles.uploadProgress__editPending}
                                title="編集準備中"
                            >
                                編集準備中
                            </span>
                        ) : error && (
                            <span className={styles.uploadProgress__status} data-status="error" title={error}>
                                {error.length > 15 ? error.substring(0, 15) + '...' : error}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UploadProgress;
